import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { HomPipesModule } from 'hom-lib/hom-pipes';
import { HomErrorLoggerModule } from 'hom-lib/hom-error-logger';
import { HomSignatureModule } from 'hom-lib/hom-signature';
import { HomEventEmitterModule } from 'hom-lib/hom-event-emitter';
import { HomFeatureLoggerModule } from 'hom-lib/hom-feature-logger';
import { HOM_ENVIRONMENT } from 'hom-lib/hom-environment';
import { environment } from './../environments/environment';

//store imports
import { rootReducers, metaReducers } from './store/reducers/root.reducer';
import { dashboardReducer } from './shared/store/dashboard';

/** Imports for the application's components, services, and modules */
// Framework Module- header - content - footer.  Layout framework is all in here.
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { FwModule } from '../fw/fw.module';
import { DevModule } from './dev/dev.module';
import { AdminModule } from './portals/admin/admin.module';
import { AdminDashboardModule } from './portals/admin-dashboard/admin-dashboard.module';
import { AcctgDashboardModule } from './portals/acctg-dashboard/acctg-dashboard.module';
import { ManagerDashboardModule } from './portals/manager-dashboard/manager-dashboard.module';
import { UserDashboardModule } from './portals/user-dashboard/user-dashboard.module';
import { WarrantyDashboardModule } from './portals/warranty-dashboard/warranty-dashboard.module';
import { UtilitiesModule } from './portals/utilities/utilities.module';
import { ProjectModule } from './portals/project/project.module';
import { InstallerModule } from './portals/installer/installer.module';
import { PortalSharedModule } from './portals/portal-shared/portal-shared.module';
import { AuthModule } from '../auth/auth.module';
import { UserJobsModule } from './portals/user/user-jobs/user-jobs.module';
import { UserSettingsModule } from './portals/user/user-settings/user-settings.module';
import { UserPendingChangesModule } from './portals/user/user-pending-changes/user-pending-changes.module';
import { UserNotificationsModule } from './portals/user/user-notifications/user-notifications.module';
import { PricingSharedModule } from './portals/pricing-shared/pricing-shared.module';
import { PoImportModule } from './portals/po-import/po-import.module';
import { FileUploadModule } from './file-upload/file-upload.module';
import { ContactModule } from './contact/contact.module';
import { SchedulerModule } from './portals/scheduler/scheduler.module';
import { SmsModule } from './sms/sms.module';

import { appProviders } from './app.providers'; //constant
import { HomComponent } from './hom/hom.component';
import { InvalidUserComponent } from './invalid-user/invalid-user.component';
import { PageNotFoundComponent } from './not-found/not-found.component';
import { LogOutComponent } from './logout/logout.component';
import { ComplianceDashboardModule } from './portals/compliance-dashboard/compliance-dashboard.module';

@NgModule({
  declarations: [
    AppComponent, // containing component for entire Angular app
    PageNotFoundComponent,
    InvalidUserComponent,
    HomComponent,
    LogOutComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PickerModule,
    HomErrorLoggerModule,
    HomPipesModule,
    HomEventEmitterModule,
    DevModule,
    HomSignatureModule,
    HomFeatureLoggerModule,
    FwModule,
    ComplianceDashboardModule,
    AdminDashboardModule,
    AcctgDashboardModule,
    ManagerDashboardModule,
    UserDashboardModule,
    UtilitiesModule,
    WarrantyDashboardModule,
    ProjectModule,
    InstallerModule,
    PortalSharedModule,
    UserSettingsModule,
    UserJobsModule,
    UserPendingChangesModule,
    UserNotificationsModule,
    PricingSharedModule,
    PoImportModule,
    FileUploadModule,
    ContactModule,
    SchedulerModule,
    AdminModule,
    SmsModule,
    BrowserAnimationsModule,
    AuthModule.forRoot(),
    StoreModule.forRoot(rootReducers, { metaReducers }),
    StoreModule.forFeature('dashboardFilters', dashboardReducer),
    // Store Devtools is a DEVELOPMENT tool and is NOT INSTRUMENTED in production. It used to be
    // instrumented unconditionally, and because `maxAge` was commented out it took NgRx's
    // default of `false` — an UNBOUNDED lifted-state history retained for the life of the tab.
    // That retention is what TODO.md §238 was filed for, and gating `instrument()` closes it.
    // `logOnly` does not help: it restricts what the history can be used for, never whether it
    // is kept. Angular flattens nested arrays in `imports`, so `[]` contributes nothing.
    // Non-production now carries an explicit bound too, so dev sessions stay finite as well.
    //
    // ⛔ THE LIBRARY IS STILL SHIPPED TO PRODUCTION — do not read the gate as tree-shaking.
    // `environment.production` is a property read on an object from another module, which esbuild
    // will not constant-fold, so both arms of the ternary stay reachable and `sideEffects: false`
    // cannot drop the package. Verified against a production build at this commit: the emitted
    // code is `le.production?[]:Jo.instrument({maxAge:25,connectInZone:!0})`, and the main chunk
    // still contains `__REDUX_DEVTOOLS_EXTENSION__`, `connectInZone`, `maxAge` and
    // `store-devtools`. Removing the payload needs a dynamic `import()` or a providers array
    // swapped by `fileReplacements` — a design change, not an edit here.
    //
    // ⚠ AND DO NOT VERIFY THIS WITH `grep StoreDevtoolsModule` ON A PRODUCTION BUNDLE. Production
    // minification renames that class (to `Jo` in the build above), so the search returns zero
    // whether or not the module ships — it would have passed with Devtools fully wired, and it
    // did. Search for string literals minification cannot rename, as listed above.
    // TODO.md §238.
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({
        maxAge: 25,
        connectInZone: true,
      }),
    EffectsModule.forRoot([]),
    //StoreRouterConnectingModule.forRoot(),  //not currently leveraging
    AppRoutingModule,
  ],
  providers: [
    appProviders,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HOM_ENVIRONMENT, useValue: environment },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
